/**
 * Shared average-salary value for the accounting view.
 *
 * Backed by localStorage and an in-module subscription so the salary
 * control and every report (which call `useAccountingView` separately)
 * stay in sync, and the chosen figure survives a reload. Per the spec
 * POC default, a flat $200k unloaded base applied to every human
 * engineer.
 *
 * Read it with `useAverageSalary()`; write it with `setAverageSalary()`.
 */

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'accounting.averageSalaryUsd';
export const DEFAULT_AVERAGE_SALARY_USD = 200_000;
export const MIN_SALARY_USD = 0;
export const MAX_SALARY_USD = 10_000_000;

function clamp(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_AVERAGE_SALARY_USD;
  return Math.min(MAX_SALARY_USD, Math.max(MIN_SALARY_USD, Math.round(n)));
}

function read(): number {
  if (typeof localStorage === 'undefined') return DEFAULT_AVERAGE_SALARY_USD;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw == null) return DEFAULT_AVERAGE_SALARY_USD;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? clamp(parsed) : DEFAULT_AVERAGE_SALARY_USD;
}

let current = read();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): number {
  return current;
}

export function setAverageSalary(value: number): void {
  const next = clamp(value);
  if (next === current) return;
  current = next;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, String(next));
  }
  for (const l of listeners) l();
}

export function useAverageSalary(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
